import React, { useState, useEffect } from "react";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Button,
  HStack,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from "@chakra-ui/react";
import { auditService } from "../services/auditService";
import { teamMappingService } from "../utils/teamMapping";

const AuditPage = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [auditReport, setAuditReport] = useState(null);

  // Load audit logs
  useEffect(() => {
    const loadAuditLogs = async () => {
      setLoading(true);
      try {
        let logs;
        if (selectedTeam) {
          logs = await auditService.getTeamAuditLogs(selectedTeam);
        } else {
          logs = await auditService.getAllAuditLogs(200);
        }
        setAuditLogs(logs);

        // Generate report
        const report = auditService.generateAuditReport(logs);
        setAuditReport(report);
      } catch (error) {
        console.error("Error loading audit logs:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAuditLogs();
  }, [selectedTeam]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return "green";
      case "UPDATE":
        return "yellow";
      case "DELETE":
        return "red";
      default:
        return "gray";
    }
  };

  const renderAuditTable = () => (
    <TableContainer>
      <Table variant="striped" colorScheme="gray" size="sm">
        <Thead>
          <Tr>
            <Th>Timestamp</Th>
            <Th>Action</Th>
            <Th>User</Th>
            <Th>Team</Th>
            <Th>Hole</Th>
            <Th>Round</Th>
            <Th>Score Change</Th>
            <Th>Details</Th>
          </Tr>
        </Thead>
        <Tbody>
          {auditLogs.map((log) => (
            <Tr key={log.id}>
              <Td fontSize="xs">{formatTimestamp(log.timestamp)}</Td>
              <Td>
                <Badge colorScheme={getActionColor(log.action)}>
                  {log.action}
                </Badge>
              </Td>
              <Td fontSize="sm">{log.userEmail}</Td>
              <Td>{log.teamId}</Td>
              <Td>{log.hole}</Td>
              <Td>{log.round}</Td>
              <Td>
                {log.action === "UPDATE" ? (
                  <Text fontSize="sm">
                    {log.oldScore} → <strong>{log.newScore}</strong>
                  </Text>
                ) : (
                  <Text fontSize="sm">
                    <strong>{log.newScore}</strong>
                  </Text>
                )}
              </Td>
              <Td fontSize="xs">
                <Text>ID: {log.scoreId}</Text>
                <Text>Session: {log.sessionId?.substring(0, 8)}...</Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const renderStats = () => {
    if (!auditReport) return null;

    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Stat>
          <StatLabel>Total Changes</StatLabel>
          <StatNumber>{auditReport.totalChanges}</StatNumber>
          <StatHelpText>All score modifications</StatHelpText>
        </Stat>

        <Stat>
          <StatLabel>Creates</StatLabel>
          <StatNumber>{auditReport.changesByAction.CREATE || 0}</StatNumber>
          <StatHelpText>New scores entered</StatHelpText>
        </Stat>

        <Stat>
          <StatLabel>Updates</StatLabel>
          <StatNumber>{auditReport.changesByAction.UPDATE || 0}</StatNumber>
          <StatHelpText>Scores modified</StatHelpText>
        </Stat>

        <Stat>
          <StatLabel>Suspicious</StatLabel>
          <StatNumber
            color={
              auditReport.suspiciousActivity.length > 0
                ? "red.500"
                : "green.500"
            }
          >
            {auditReport.suspiciousActivity.length}
          </StatNumber>
          <StatHelpText>Large changes (&gt;3 strokes)</StatHelpText>
        </Stat>
      </SimpleGrid>
    );
  };

  const renderSuspiciousActivity = () => {
    if (!auditReport?.suspiciousActivity.length) {
      return (
        <Alert status="success">
          <AlertIcon />
          No suspicious activity detected!
        </Alert>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {auditReport.suspiciousActivity.map((activity, index) => (
          <Alert key={index} status="warning">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">
                {activity.userEmail} - Team {activity.teamId}, Hole{" "}
                {activity.hole}, Round {activity.round}
              </Text>
              <Text fontSize="sm">{activity.reason}</Text>
              <Text fontSize="xs">{formatTimestamp(activity.timestamp)}</Text>
            </Box>
          </Alert>
        ))}
      </VStack>
    );
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading audit logs...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>
            Score Audit Logs
          </Heading>
          <Text color="gray.600">
            Complete history of all score changes with user accountability
          </Text>
        </Box>

        {/* Filters */}
        <HStack spacing={4}>
          <Select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            placeholder="All Teams"
            maxW="200px"
          >
            {teamMappingService.getAllTeams().map((team) => (
              <option key={team.id} value={team.id}>
                Team {team.id}: {team.name}
              </option>
            ))}
          </Select>

          <Button
            onClick={() => setSelectedTeam("")}
            variant="outline"
            size="sm"
          >
            Clear Filter
          </Button>
        </HStack>

        {/* Tabs */}
        <Tabs variant="enclosed" colorScheme="green">
          <TabList>
            <Tab>Overview</Tab>
            <Tab>Audit Log</Tab>
            <Tab>Suspicious Activity</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" mb={4}>
                    Statistics
                  </Heading>
                  {renderStats()}
                </Box>

                {auditReport && (
                  <Box>
                    <Heading size="md" mb={4}>
                      Activity by User
                    </Heading>
                    <TableContainer>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>User</Th>
                            <Th isNumeric>Changes</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {Object.entries(auditReport.changesByUser).map(
                            ([user, count]) => (
                              <Tr key={user}>
                                <Td>{user}</Td>
                                <Td isNumeric>{count}</Td>
                              </Tr>
                            )
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            {/* Audit Log Tab */}
            <TabPanel>
              <Box>
                <Text mb={4}>
                  Showing {auditLogs.length} audit entries
                  {selectedTeam && ` for Team ${selectedTeam}`}
                </Text>
                {renderAuditTable()}
              </Box>
            </TabPanel>

            {/* Suspicious Activity Tab */}
            <TabPanel>
              <Box>
                <Heading size="md" mb={4}>
                  Suspicious Activity
                </Heading>
                <Text mb={4} color="gray.600">
                  Score changes greater than 3 strokes are flagged for review
                </Text>
                {renderSuspiciousActivity()}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default AuditPage;
